// Automation template types for partner-dashboard
// These match the structure from internal-dashboard

import { AutomationTemplateContents } from '@reach/shared-types/automation-template';

/**
 * UI-level tenant selection mode:
 * - 'all': sync to all current and future tenants (maps to targetedTenantsFilter: null)
 * - 'selected': sync only to selected tenants (maps to targetedTenantsFilter: string[])
 */
export type TenantSelectionType = 'all' | 'selected';

/**
 * Converts the backend `targeted_tenants_filter` (null=all, []=none, string[]=filtered)
 * to the UI-level TenantSelectionType.
 */
export function getTenantSelectionType(
  targetedTenantsFilter: string[] | null | undefined,
): TenantSelectionType {
  return targetedTenantsFilter == null ? 'all' : 'selected';
}

/**
 * Converts UI-level tenant selection state to the API's `targetedTenantsFilter` value.
 */
export function toTargetedTenantsFilter(
  type: TenantSelectionType,
  selectedIds: string[],
): string[] | null {
  return type === 'all' ? null : selectedIds;
}

export interface AutomationTemplate {
  id: string;
  name: string;
  version: string;
  contents: AutomationTemplateContents;
  platform_id: string;
  created_at: string;
  updated_at: string;
  visibility?: 'visible' | 'hidden';
  targeted_tenants_filter?: string[] | null;
  /**
   * Business IDs actively using this template (live/on). Present on the list response
   * (GET /partner/automation-templates); absent on single-template responses.
   */
  activeBusinessIds?: string[];
}

export interface CreateAutomationTemplateParams {
  name: string;
  version: string;
  contents: AutomationTemplateContents;
}

export interface UpdateAutomationTemplateParams {
  name?: string;
  version?: string;
  contents?: Partial<AutomationTemplateContents>;
  targetedTenantsFilter?: string[] | null;
}

export interface TemplatesResponse {
  templates: AutomationTemplate[];
}

export interface TemplateResponse {
  template: AutomationTemplate;
}

export interface DuplicateDefaultsResponse {
  success: boolean;
  createdCount: number;
  templateIds?: string[];
}

export interface TenantSegmentConflict {
  tenantId: string;
  tenantName: string;
  missingSegments: Array<{ canonicalVersionId: string; name: string }>;
}

export interface ValidateTenantSegmentsResponse {
  conflicts: TenantSegmentConflict[];
}

export interface TenantSelectionConflict {
  tenantId: string;
  tenantName: string;
  description: string;
}

/** GET /sync/status/:templateId — returns status and subJobs when a matching active job exists. */
export interface CheckActiveSyncJobsResponse {
  hasActiveJobs: boolean;
  status?: string;
  subJobs?: Array<{ jobId: string; businessId: string | null; status: string }>;
}

export interface TenantAutomationStatus {
  id: string;
  status: string;
  dryRun: boolean;
}

export interface BusinessAutomationStatusRow {
  businessId: string;
  businessName: string;
  automations: TenantAutomationStatus[];
  statusCounts: Record<string, number>;
}

export interface AutomationStatusesResponse {
  businessStatuses: BusinessAutomationStatusRow[];
  totalAutomations: number;
}
